import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, Table } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import dayjs from '../util/date.js';
import LabeledCheckbox from './LabeledCheckbox.jsx';
import { trackEvent } from '../util/Analytics.jsx';
import Utils from '../util/Utils.jsx';

import { useStateValue } from '../context/state.jsx';
import { setProjectActivities } from '../context/datasets.js';
import { openAlert } from '../context/alert.js';
import { setProgress } from '../offline-features/setProgress.js';

const ActivityProgressDetails = ({
	datasetId,
	activity,
	datasetInfo,
	setUnsavedChanges,
	hasUnsavedChanges
}) => {
	// Refs
	const startFlatpickr = useRef(null);
	const endFlatpickr = useRef(null);

	// Global state
	const [{
		datasets,
		projectActivities,
		projectActivitiesMap
	}, dispatch] = useStateValue();

	// Local state
	const hoursPerDay = parseFloat(datasetInfo.HoursPerDay) || 1;

	const getActivityDataForState = () => {
		const projects = datasetInfo.projects && !Array.isArray(datasetInfo.projects)
			? datasetInfo.projects
			: null;
		const projectMeta = projects && projects[activity.projId];
		return {
			dataDate: dayjs(projectMeta && projectMeta.dataDate).valueOf(),
			statusCode: activity.statusCode,
			actualStart: activity.actualStart,
			actualFinish: activity.actualFinish,
			targetStart: activity.targetStart,
			targetFinish: activity.targetFinish,
			earlyFinish: activity.earlyFinish,
			expectFinish: activity.expectFinish,
			expectFinishApply: activity.expectFinish !== 0,
			start: activity.start,
			finish: activity.finish,
			remainingDuration: (activity.remainingDuration / hoursPerDay).toFixed(1),
			targetDuration: (activity.targetDuration / hoursPerDay).toFixed(1),
			percentComplete: activity.percentComplete.toFixed(1),
			hoursPerDay
		};
	};

	const [{
		dataDate,
		statusCode,
		actualStart,
		actualFinish,
		targetStart,
		targetFinish,
		earlyFinish,
		expectFinish,
		expectFinishApply,
		start,
		finish,
		remainingDuration,
		targetDuration,
		percentComplete
	}, setState] = useState(getActivityDataForState());

	useEffect(() => {
		setState(getActivityDataForState());
		setUnsavedChanges(false);
	}, [activity.activityId, activity.hasChanged, activity.statusCode, activity.start, activity.finish, activity.actualStart, activity.actualFinish, activity.percentComplete, activity.remainingDuration]);

	const handleIsStartedChange = e => {
		if (statusCode === 'TK_NotStart' && e.target.checked) {
			if (activity.taskType === 'TT_Mile') {
				setState(prevState => ({
					...prevState,
					actualStart: targetStart,
					statusCode: 'TK_Complete',
					percentComplete: '100.0'
				}));
				setUnsavedChanges(true);
			} else {
				setState(prevState => ({
					...prevState,
					actualStart: targetStart,
					statusCode: 'TK_Active'
				}));
				setUnsavedChanges(true);
			}
		} else if ((statusCode === 'TK_Active' || statusCode === 'TK_Complete') && !e.target.checked) {
			if (percentComplete > 0) {
				dispatch(openAlert('Unstart', 'Are you sure you want unstart this task?',
					[{
						text: 'No, ignore the change',
						action: () => true
					}, {
						text: 'Yes, unstart this task',
						action: unstartTask
					}]
				));
			} else {
				unstartTask();
			}
		}
	};

	const unstartTask = () => {
		setState(prevState => ({
			...prevState,
			statusCode: 'TK_NotStart',
			start: targetStart,
			finish: targetFinish,
			actualStart: 0,
			actualFinish: 0,
			remainingDuration: targetDuration,
			percentComplete: '0.0'
		}));
		setUnsavedChanges(true);
	};

	const handleIsFinishedChange = e => {
		if (statusCode === 'TK_NotStart' && e.target.checked) {
			setState(prevState => ({
				...prevState,
				actualStart: targetStart,
				actualFinish: targetFinish,
				expectFinish: 0,
				statusCode: 'TK_Complete',
				remainingDuration: '0.0',
				percentComplete: '100.0'
			}));
			setUnsavedChanges(true);
		} else if (statusCode === 'TK_Active' && e.target.checked) {
			setState(prevState => ({
				...prevState,
				actualFinish: finish,
				expectFinish: 0,
				statusCode: 'TK_Complete',
				remainingDuration: '0.0',
				percentComplete: '100.0'
			}));
			setUnsavedChanges(true);
		} else if (statusCode === 'TK_Complete' && !e.target.checked) {
			dispatch(openAlert('Uncomplete', 'Are you sure you want uncomplete this task?',
				[{
					text: 'No, ignore the change',
					action: () => true
				}, {
					text: 'Yes, uncomplete this task',
					action: activity.taskType === 'TT_FinMile'
						? () => {
							setState(prevState => ({
								...prevState,
								actualFinish: 0,
								statusCode: 'TK_NotStart',
								percentComplete: '0.0',
								finish: earlyFinish
							}));
							setUnsavedChanges(true);
						}
						: () => {
							setState(prevState => ({
								...prevState,
								actualFinish: 0,
								statusCode: 'TK_Active',
								remainingDuration: (activity.remainingDuration / hoursPerDay).toFixed(1),
								percentComplete: activity.percentComplete.toFixed(1),
								finish: Math.max(earlyFinish, expectFinish)
							}));
							setUnsavedChanges(true);
						}
				}]
			));
		}
	};

	const triggerStartDateInput = () => {
		if (activity.taskType !== 'TT_LOE') {
			startFlatpickr.current.flatpickr.open();
		}
	};

	const handleStartDateChange = (dates, string, flatpickr) => {
		const d = dates[0].valueOf();
		flatpickr.close(); // Necessary for some reason for mobile browsers

		if (statusCode === 'TK_NotStart') {
			const delta = dayjs(d).diff(start, 'days'),
				finishClone = dayjs(finish).add(delta, 'days');

			setState(prevState => ({
				...prevState,
				start: d,
				finish: finishClone.valueOf(),
				targetStart: d,
				targetFinish: finishClone.valueOf()
			}));
			setUnsavedChanges(true);
		} else {
			if (d > finish && activity.taskType !== 'TT_Mile') {
				dispatch(openAlert('Invalid Start Date', 'The start date must be less than the finish date.'));
				startFlatpickr.current.flatpickr.setDate(start);
				return;
			}

			if (statusCode === 'TK_Active') {
				const dataDateClone = dayjs(dataDate).add(remainingDuration, 'days');

				setState(prevState => ({
					...prevState,
					start: d,
					finish: dataDateClone.valueOf(),
					actualStart: d,
					earlyFinish: dataDateClone.valueOf()
				}));
				setUnsavedChanges(true);
			} else if (statusCode === 'TK_Complete') {
				setState(prevState => ({
					...prevState,
					start: d,
					actualStart: d
				}));
				setUnsavedChanges(true);
			}
		}
	};

	const triggerFinishDateInput = () => {
		if (activity.taskType !== 'TT_LOE') {
			endFlatpickr.current.flatpickr.open();
		}
	};

	const handleFinishDateChange = (dates, string, flatpickr) => {
		const d = dates[0].valueOf();
		flatpickr.close(); // Necessary for some reason for mobile browsers

		if (statusCode === 'TK_NotStart') {
			const delta = dayjs(d).diff(finish, 'days'),
				startClone = dayjs(start).add(delta, 'days');

			setState(prevState => ({
				...prevState,
				start: startClone.valueOf(),
				finish: d,
				targetStart: startClone.valueOf(),
				targetFinish: d
			}));
			setUnsavedChanges(true);
		} else {
			if (d < start) {
				dispatch(openAlert('Invalid Finish Date', 'The finish date must be greater than the start date.'));
				endFlatpickr.current.flatpickr.setDate(finish);
				return;
			}

			if (statusCode === 'TK_Active') {
				setState(prevState => ({
					...prevState,
					finish: d,
					targetFinish: d,
					expectFinish: d,
					remainingDuration: dayjs(d).diff(dayjs(Math.min(dataDate, start)), 'days').toFixed(1)
				}));
				setUnsavedChanges(true);
			} else if (statusCode === 'TK_Complete') {
				setState(prevState => ({
					...prevState,
					finish: d,
					actualFinish: d
				}));
				setUnsavedChanges(true);
			}
		}
	};

	const handleRemainingDurationChange = e => {
		const startClone = dayjs(start).add(e.target.value, datasetInfo.RemainingDurationUnit),
			remainingDurationCopy = e.target.value,
			targetDurationCopy = statusCode === 'TK_NotStart' ? remainingDurationCopy : targetDuration;
		let percentCompleteCopy = percentComplete;

		if (activity.completePercentType === 'CP_Drtn') {
			percentCompleteCopy = (100 - remainingDurationCopy / targetDurationCopy * 100).toFixed(1);
			if (percentComplete < 0) percentCompleteCopy = 0.0;
			if (percentComplete > 100) percentCompleteCopy = 100.0;
		}

		setState(prevState => ({
			...prevState,
			finish: startClone.valueOf(),
			targetFinish: startClone.valueOf(),
			percentComplete: percentCompleteCopy,
			remainingDuration: remainingDurationCopy,
			targetDuration: targetDurationCopy
		}));
		setUnsavedChanges(true);
	};

	const handlePercentCompleteChange = e => {
		let remainingDurationCopy = remainingDuration;
		const percentCompleteCopy = e.target.value;

		if (activity.completePercentType === 'CP_Drtn') {
			remainingDurationCopy = (targetDuration * (100 - percentCompleteCopy) / 100).toFixed(1);
			if (remainingDuration < 0) remainingDurationCopy = 0.0;
			if (remainingDuration > 100) remainingDurationCopy = 100.0;
		}

		setState(prevState => ({
			...prevState,
			remainingDuration: remainingDurationCopy,
			percentComplete: percentCompleteCopy
		}));
		setUnsavedChanges(true);
	};

	const updateProgress = () => {
		const progressData = {
			activityId: activity.activityId,
			name: activity.name,
			statusCode,
			start,
			finish,
			actualStart,
			actualFinish,
			targetStart,
			targetFinish,
			earlyFinish,
			expectFinish: expectFinishApply ? expectFinish : 0,
			remainingDuration: remainingDuration * hoursPerDay,
			targetDuration: targetDuration * hoursPerDay,
			percentComplete,
			taskType: activity.taskType
		};
		const allowOffline = datasets[datasetId].AllowOffline;
		setProgress(dispatch, datasetId, allowOffline, progressData);
		setUnsavedChanges(false);
	};

	const revertActivityConfirmation = () => {
		if (!activity.hasChanged) {
			dispatch(openAlert(
				'Nothing to revert',
				'This activity has no SiteWalker changes since the most recent import.'
			));
			return;
		}
		dispatch(openAlert('Revert to import?', 'This will undo any changes (including added pictures) since the most recent import. Continue?',
			[{
				text: 'No, cancel',
				action: () => true
			}, {
				bsStyle: 'danger',
				text: 'Revert to import',
				action: () => {
					revertActivity(activity.activityId);
				}
			}]
		));
	};

	const revertActivity = activityId => {
		trackEvent('activity', 'click', 'revert activity');

		const projectActivitiesCopy = [...projectActivities];
		const activities = String(activityId);

		Utils.ajax('revertActivity', { dataset: datasetId, activities }, dispatch, () => {
			Utils.ajax('getActivityInfo', { dataset: datasetId, activities }, dispatch, result => {
				if (result.success) {
					for (const nextActivity of result.activities) {
						projectActivitiesCopy[projectActivitiesMap[nextActivity.activityId]] = nextActivity;
					}
					dispatch(setProjectActivities(projectActivitiesCopy));
					// Local progress fields refresh after parent re-renders with reverted activity.
					setUnsavedChanges(false);
					dispatch(openAlert('Reversion', 'Reversion to the most recently imported state has completed.'));
				}
			});
		});
	};

	return (
		<div className="activityProgressDetails">
			<Table className="activityProgressTable">
				{activity.taskType === 'TT_FinMile'
					? null
					: (
						<tbody>
							<tr>
								<td>
									<LabeledCheckbox
										text="Started"
										checked={statusCode !== 'TK_NotStart'}
										onChange={handleIsStartedChange}
										disabled={activity.taskType === 'TT_LOE'} />
								</td>
								<td className="datePickerColumn">
									<Flatpickr
										ref={startFlatpickr}
										className="datePicker startDate form-control"
										options={{
											dateFormat: 'd-M-Y',
											onChange: handleStartDateChange
										}}
										value={start}
										disabled={activity.taskType === 'TT_LOE'}
										onClick={triggerStartDateInput} />
								</td>
							</tr>
						</tbody>
					)}
				{activity.taskType === 'TT_Mile'
					? null
					: (
						<tbody>
							<tr>
								<td>
									<LabeledCheckbox
										text="Finished"
										checked={statusCode === 'TK_Complete'}
										onChange={handleIsFinishedChange}
										disabled={activity.taskType === 'TT_LOE'} />
								</td>
								<td className="datePickerColumn">
									<Flatpickr
										ref={endFlatpickr}
										className="datePicker endDate form-control"
										options={{
											dateFormat: 'd-M-Y',
											onChange: handleFinishDateChange
										}}
										value={finish}
										disabled={activity.taskType === 'TT_LOE'}
										onClick={triggerFinishDateInput} />
								</td>
							</tr>
						</tbody>
					)}
				{/*Debug info*/
				/*<tbody>
					<tr><td colSpan="2">Data Date: {dataDate ? dayjs(dataDate).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Actual Start: {actualStart ? dayjs(actualStart).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Actual Finish: {actualFinish ? dayjs(actualFinish).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Target Start: {targetStart ? dayjs(targetStart).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Target Finish: {targetFinish ? dayjs(targetFinish).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Early Finish: {earlyFinish ? dayjs(earlyFinish).format('DD-MMM-YYYY') : 0}</td></tr>
					<tr><td colSpan="2">Expected Finish: {expectFinish ? dayjs(expectFinish).format('DD-MMM-YYYY') : 0}</td></tr>
				</tbody>*/}
				{activity.taskType === 'TT_FinMile' || activity.taskType === 'TT_Mile'
					? null
					: (
						<tbody>
							<tr>
								<td>Remaining Duration</td>
								<td>
									<FormControl
										type="number"
										value={remainingDuration}
										onChange={handleRemainingDurationChange}
										disabled={
											activity.taskType === 'TT_LOE' ||
										(statusCode === 'TK_NotStart' && (!datasetInfo.AllowNotStartDurationChanges || (!('7' in datasetInfo.Permissions)))) ||
										statusCode === 'TK_Complete'
										}>
									</FormControl>
								</td>
							</tr>
							<tr>
								<td>% Complete</td>
								<td>
									<FormControl
										type="number"
										value={percentComplete}
										onChange={handlePercentCompleteChange}
										disabled={statusCode !== 'TK_Active' || activity.taskType === 'TT_LOE'}
										onFocus={e => { e.target.select(); }}>
									</FormControl>
								</td>
							</tr>
							{activity.taskType === 'TT_Mile'
								? null
								: (
									<tr>
										<td>Expected Finish</td>
										<td>
											<LabeledCheckbox
												text="Apply"
												checked={expectFinishApply}
												onChange={() => {
													setState(prevState => ({
														...prevState,
														expectFinishApply: !expectFinishApply
													}));
												}}
												disabled={statusCode !== 'TK_Active'} />
										</td>
									</tr>
								)}
						</tbody>
					)}
				<tbody>
					<tr>
						<td>
							<Button
								block
								bsStyle="danger"
								onClick={revertActivityConfirmation}>
								<span className="glyphicon glyphicon-trash leftGlyph"></span>
								Revert
							</Button>
						</td>
						<td>
							<Button
								block
								bsStyle="primary"
								onClick={updateProgress}
								disabled={!hasUnsavedChanges()}>
								<span className="glyphicon glyphicon-cloud-upload leftGlyph"></span>
								Update Progress
							</Button>
						</td>
					</tr>
				</tbody>
			</Table>
		</div>
	);
};

ActivityProgressDetails.propTypes = {
	datasetId: PropTypes.string,
	activity: PropTypes.object.isRequired,
	datasetInfo: PropTypes.object.isRequired,
	setUnsavedChanges: PropTypes.func,
	hasUnsavedChanges: PropTypes.func
};

export default ActivityProgressDetails;